import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import _ from "lodash";
import cx from 'classnames';

import { EditorContext } from "components/Editor";
import { createWidget, selectWidget } from "actions/editor";
import { useHotspotParentSelector, useSelectedWidgetSelector, useWidgetChildrenSelector } from "components/Editor/hooks/useEditorSession";
import { useContainerSelector } from "components/Editor/hooks/useEditorSession";
import { getStylesForLayout } from "components/Editor/helper/layoutManager";
import { PageWidgetContext } from "../page";
import { CAROUSEL, FORM, FORM_STEP, SECTION, TEXT } from "constants/widgetTypes";
import { defaultOtpWidgets } from "components/Palette/form";
import StoryboardContext from "components/Storyboard/context";

import { WidgetRegistry } from '../registry';

import styles from '../../editor.module.scss';

let isFreeSize = false;

export const Widget = ({ widgetProps, ...rest }) => {
  const dispatch = useDispatch();
  const ref = React.useRef(null);

  const id = _.get(widgetProps, 'uid');

  const storyboardContext = React.useContext(StoryboardContext);
  const storyboardType = _.get(storyboardContext, 'type', '');
  const type = _.get(widgetProps, 'type');
  const editorContext = React.useContext(EditorContext);
  const editor = editorContext.editorSession.id;
  const selectedWidget = useSelectedWidgetSelector(editor)?.[0];

  const pageWidgetContext = React.useContext(PageWidgetContext);

  const hasHotspotChildren = useHotspotParentSelector(editor, widgetProps);

  const breakpoint = useSelector(state => _.get(state, `${editor}.present.breakpoint`));
  const selectedPage = useSelector(state => _.get(state, `${editor}.present.selectedPage`));
  const container = useContainerSelector(editor, id);
  
  var widgetChildren = useWidgetChildrenSelector(editor, id) || [];
  widgetChildren = widgetChildren.filter(item => {
    return _.get(item, `properties.${breakpoint}.visible`, true);
  });

  otpForOldForms(widgetProps, widgetChildren, dispatch, editor);

  widgetChildren = _.orderBy(widgetChildren, 'order', 'asc');

  if (type == CAROUSEL) {
    isFreeSize = _.get(widgetProps, 'properties.desktop.free');
  }
  
  const layout = _.get(widgetProps, `properties.${breakpoint}.layout`);
  var widgetStyle = React.useMemo(() => {
    try {
      return getStylesForLayout(layout, breakpoint, container, storyboardType, widgetProps.type, isFreeSize);
    } catch (e) {
      console.error(e);
      return {};
    }
  }, [layout, breakpoint, container, isFreeSize]);

  useEffect(() => {
    return () => {
      if(editorContext.editorSession.moveableManager.current){
        editorContext.editorSession.moveableManager.current.updateRect();
      }
    }
  }, []);

  useEffect(() => {
    if (ref.current) {
      const frame = editorContext.editorSession.moveableData.createFrame(ref.current, layout);
      editorContext.editorSession.moveableData.render(ref.current, frame);

      if (editorContext.editorSession.moveableManager.current) {
        editorContext.editorSession.moveableManager.current.updateRect();
      }
    }

    if (hasHotspotChildren) {
      ref.current && ref.current.setAttribute("data-hotspot", true);
    } else {
      ref.current && ref.current.removeAttribute("data-hotspot");
    }
  }, [breakpoint, layout, widgetStyle]);

  const layoutDependency = useMemo(() => {
    if (['RESOURCE_CENTER', 'LANDING_PAGE'].includes(storyboardType)) {
      if (type === TEXT){
        const selectedTextWidgetElement  = document.getElementById(id);
        if (selectedTextWidgetElement){
          const selectedWidgetRect = selectedTextWidgetElement.getBoundingClientRect();
          return selectedWidgetRect.height;
        }
      }
      return layout?.height;
    }
    return layout;
  }, [layout, storyboardType, type]);

  const handleWidgetOveflow = (id, selectedPage ) => {
     const selectedPageElement = document.getElementById(selectedPage);
     const selectedWidgetElement  = document.getElementById(id);
     if (selectedPageElement && selectedWidgetElement){
      const selectedPageRect = selectedPageElement.getBoundingClientRect();
      const selectedWidgetRect = selectedWidgetElement.getBoundingClientRect();
      if (selectedPageRect.height > (selectedWidgetRect.bottom - selectedPageRect.top)){
        return;
      }
      pageWidgetContext.onResize();
    }
  }
  
  useEffect(() => {
    if (!selectedWidget && ['RESOURCE_CENTER', 'LANDING_PAGE'].includes(storyboardType)) {
      return;
    }
  
    if (['RESOURCE_CENTER', 'LANDING_PAGE'].includes(storyboardType)) {
      handleWidgetOveflow(id, selectedPage, pageWidgetContext);
      return;
    }
  
    pageWidgetContext.onResize();
  }, [breakpoint, pageWidgetContext.onResize, storyboardType, layoutDependency]);

  const Component = getWidgetComponentFotType(type);
  if(!Component){
    console.warn(`Component not found for type ${type}`);
    return false;
  }

  var widgetStyle = getStylesForLayout(layout, breakpoint, container, storyboardType, widgetProps.type, isFreeSize);

  var children = widgetChildren?.map(item => {
    return <Widget key={item.uid} widgetProps={item} editable />
  });

  return React.createElement(Component, {
    key: id,
    widgetRef: ref,
    widgetProps: widgetProps, 
    className: cx(styles.widget, 'widget'),
    style: widgetStyle,
    ...rest
  }, children);
}

function getWidgetComponentFotType(type) {
  var config = WidgetRegistry.widgets[type];
  if (!config) {
    console.error(`Widget config for type ${type} not found. Check if the widget type is registered`);
    return false;
  }

  return config['editor'];
}

function otpForOldForms(widgetProps, widgetChildren, dispatch, editor) {
  if (_.get(widgetProps, 'type') === FORM) {
    if (widgetChildren?.length === 2) {
      const otpState = {
        type: FORM_STEP,
        name: "OTP",
        properties: {
          "desktop": {
            "visible": true,
            "layout": {
              "transform": {
                "translate": "0px, 0px",
                "rotate": "0deg"
              },
              "width": null,
              "height": null
            }
          },
          "mobile": {
            "visible": true,
            "layout": {
              "transform": {
                "translate": "0px, 0px",
                "rotate": "0deg"
              },
              "width": null,
              "height": null
            }
          }
        }
      };
      dispatch(createWidget(editor, [otpState], widgetProps.uid));
      dispatch(selectWidget(editor, []));
    }
  }
  if (_.get(widgetProps, 'type') === FORM_STEP && _.get(widgetProps, 'name') === 'OTP') {
    if (widgetChildren?.length === 0) {
      const otpWidgets = defaultOtpWidgets;
      dispatch(createWidget(editor, otpWidgets, widgetProps.uid));
      dispatch(selectWidget(editor, []));
    }
  }
}